import API_PATHS from "../api/apiPaths";
import { contactDetailsPayload, ERROR_CODES } from "../utils/constant";
import { mapErrorToConfig } from "../utils/errorMapper";
import { eventBus } from "../utils/eventBus";
import { EVENTS } from "../utils/eventTypes";
import { apiClient } from "./apiClient";
import { setUserData } from "./userStore";

export const initializeApiHandler = () => {
  eventBus.on(EVENTS.FETCH_CUSTOMER_TOKEN, async (customer) => {
    try {
      const response = await apiClient.post(`/customer/token`, customer);
      const data = await response.json();
      eventBus.emit(EVENTS.FETCH_CUSTOMER_TOKEN_SUCCESS, data);
    } catch (error) {
      eventBus.emit(EVENTS.FETCH_CUSTOMER_TOKEN_ERROR, error);
    }
  });
};

export const initializeContactUsHandler = () => {
  const contactUsHandler = async () => {
    try {
      const response = await apiClient.post(
        API_PATHS.CONTACT_US.GET_CONTACT_DETAILS,
        contactDetailsPayload,
      );
      const data = response.data;

      if (data.code === ERROR_CODES.SUCCESS) {
        eventBus.emit(EVENTS.FETCH_CONTACT_US_SUCCESS, data);
      } else {
        console.error("API Error:", data);
        eventBus.emit(EVENTS.FETCH_API_ERROR, data.code);
      }
    } catch (error) {
      console.log("Fetch Contact Us Error:", error);
      let errorCode = error?.data?.code;
      if (!errorCode && error?.code === "ECONNABORTED") {
        errorCode = 30001;
      }
      eventBus.emit(EVENTS.FETCH_API_ERROR, errorCode);
    }
  };
  eventBus.on(EVENTS.FETCH_CONTACT_US, contactUsHandler);
  return () => {
    eventBus.off(EVENTS.FETCH_CONTACT_US, contactUsHandler);
  };
};

export const initializeMyProfileHandler = () => {
  const myProfileHandler = async () => {
    try {
      const response = await apiClient.get(API_PATHS.MY_PROFILE.GET_MY_PROFILE);
      const data = response.data;

      if (data.code === ERROR_CODES.SUCCESS) {
        eventBus.emit(EVENTS.FETCH_MY_PROFILE_SUCCESS, data);
      } else {
        eventBus.emit(EVENTS.FETCH_API_ERROR, data.code);
      }
    } catch (error) {
      let errorCode = error?.data?.code;
      // fallback for timeout / aborted request
      if (!errorCode && error?.code === "ECONNABORTED") {
        errorCode = 30001;
      }
      eventBus.emit(EVENTS.FETCH_API_ERROR, errorCode);
    }
  };
  eventBus.on(EVENTS.FETCH_MY_PROFILE, myProfileHandler);
  return () => {
    eventBus.off(EVENTS.FETCH_MY_PROFILE, myProfileHandler);
  };
};

export const initializeMyMortgageHandler = () => {
  const myMortgageHandler = async () => {
    try {
      const response = await apiClient.get(
        API_PATHS.MY_MORTGAGE.GET_MY_MORTGAGE,
      );
      const data = response.data;

      if (data.code === ERROR_CODES.SUCCESS) {
        eventBus.emit(EVENTS.FETCH_MY_MORTGAGE_SUCCESS, data);
      } else {
        eventBus.emit(EVENTS.FETCH_API_ERROR, data.code);
      }
    } catch (error) {
      let errorCode = error?.data?.code;
      console.log("Fetch My Mortgage Error:", error);
      if (!errorCode && error?.code === "ECONNABORTED") {
        console.log(
          "Request timed out or was aborted. Setting error code to 30001.",
        );
        errorCode = 30001;
      }
      eventBus.emit(EVENTS.FETCH_API_ERROR, errorCode);
    }
  };
  eventBus.on(EVENTS.FETCH_MY_MORTGAGE, myMortgageHandler);
  return () => {
    eventBus.off(EVENTS.FETCH_MY_MORTGAGE, myMortgageHandler);
  };
};

export const initializeHomePageHandler = () => {
  const homePageHandler = async () => {
    try {
      const response = await apiClient.get(API_PATHS.HOME_PAGE.GET_HOME_PAGE);
      const data = response.data;
      if (data.code === ERROR_CODES.SUCCESS) {
        eventBus.emit(EVENTS.FETCH_HOME_PAGE_SUCCESS, data);
      } else {
        eventBus.emit(EVENTS.FETCH_API_ERROR, data.code);
      }
    } catch (error) {
      let errorCode = error?.data?.code;
      if (!errorCode && error?.code === "ECONNABORTED") {
        errorCode = 30001;
      }
      eventBus.emit(EVENTS.FETCH_API_ERROR, errorCode);
    }
  };
  eventBus.on(EVENTS.FETCH_HOME_PAGE, homePageHandler);
  return () => {
    eventBus.off(EVENTS.FETCH_HOME_PAGE, homePageHandler);
  };
};

export const initializeSaveYourPreferencesHandler = () => {
  const savePreferencesHandler = async (payload) => {
    try {
      const response = await apiClient.post(
        API_PATHS.MY_PROFILE.SAVE_YOUR_PREFERENCES,
        payload,
        { skipGlobalErrorHandler: true },
      );

      const data = response.data;

      if (data.code === ERROR_CODES.SUCCESS) {
        eventBus.emit(EVENTS.FETCH_SAVE_YOUR_PREFERENCES_SUCCESS, data);
      } else {
        eventBus.emit(EVENTS.FETCH_SAVE_YOUR_PREFERENCES_ERROR, data);
      }
    } catch (error) {
      eventBus.emit(EVENTS.FETCH_SAVE_YOUR_PREFERENCES_ERROR, {
        message: "Your update has failed. Please check and try again.",
        code:
          error?.response?.data?.code ||
          (error?.code === "ECONNABORTED" ? 30001 : 30001),
      });
    }
  };

  eventBus.on(EVENTS.FETCH_SAVE_YOUR_PREFERENCES, savePreferencesHandler);

  // IMPORTANT CLEANUP
  return () => {
    eventBus.off(EVENTS.FETCH_SAVE_YOUR_PREFERENCES, savePreferencesHandler);
  };
};

export const initializeYourPreferencesHandler = () => {
  const yourPreferencesHandler = async () => {
    try {
      const response = await apiClient.get(
        API_PATHS.MY_PROFILE.GET_YOUR_PREFERENCES,
      );
      const data = response.data;

      if (data.code === ERROR_CODES.SUCCESS) {
        eventBus.emit(EVENTS.FETCH_YOUR_PREFERENCES_SUCCESS, data);
      } else {
        eventBus.emit(EVENTS.FETCH_API_ERROR, data.code);
      }
    } catch (error) {
      let errorCode = error?.data?.code;
      if (!errorCode && error?.code === "ECONNABORTED") {
        errorCode = 30001;
      }
      eventBus.emit(EVENTS.FETCH_API_ERROR, errorCode);
    }
  };
  eventBus.on(EVENTS.FETCH_YOUR_PREFERENCES, yourPreferencesHandler);
  return () => {
    eventBus.off(EVENTS.FETCH_YOUR_PREFERENCES, yourPreferencesHandler);
  };
};

export const initializeYourToDoListsHandler = () => {
  const yourToDoListsHandler = async () => {
    try {
      const response = await apiClient.get(
        API_PATHS.HOME_PAGE.GET_YOUR_TO_DO_LISTS,
      );
      const data = response.data;

      if (data.code === ERROR_CODES.SUCCESS) {
        eventBus.emit(EVENTS.FETCH_YOUR_TODO_LISTS_SUCCESS, data);
      } else {
        eventBus.emit(EVENTS.FETCH_API_ERROR, data.code);
      }
    } catch (error) {
      let errorCode = error?.data?.code;
      if (!errorCode && error?.code === "ECONNABORTED") {
        errorCode = 30001;
      }
      eventBus.emit(EVENTS.FETCH_API_ERROR, errorCode);
    }
  };
  eventBus.on(EVENTS.FETCH_YOUR_TODO_LISTS, yourToDoListsHandler);
  return () => {
    eventBus.off(EVENTS.FETCH_YOUR_TODO_LISTS, yourToDoListsHandler);
  };
};

export const initializeYourToDoListsPreferencesHandler = () => {
  const yourToDoListsPreferencesHandler = async () => {
    try {
      const response = await apiClient.get(
        API_PATHS.HOME_PAGE.GET_YOUR_TO_DO_LISTS_PREFERENCES,
      );
      const data = response.data;

      if (data.code === ERROR_CODES.SUCCESS) {
        eventBus.emit(EVENTS.FETCH_YOUR_TODO_LISTS_PREFERENCES_SUCCESS, data);
      } else {
        eventBus.emit(EVENTS.FETCH_API_ERROR, data.code);
      }
    } catch (error) {
      let errorCode = error?.data?.code;
      if (!errorCode && error?.code === "ECONNABORTED") {
        errorCode = 30001;
      }
      eventBus.emit(EVENTS.FETCH_API_ERROR, errorCode);
    }
  };
  eventBus.on(
    EVENTS.FETCH_YOUR_TODO_LISTS_PREFERENCES,
    yourToDoListsPreferencesHandler,
  );
  return () => {
    eventBus.off(
      EVENTS.FETCH_YOUR_TODO_LISTS_PREFERENCES,
      yourToDoListsPreferencesHandler,
    );
  };
};

export const initializeMyDocumentsHandler = () => {
  const myDocumentshandler = async () => {
    try {
      const response = await apiClient.get(
        API_PATHS.MY_DOCUMENTS.GET_MY_DOCUMENTS,
      );
      const data = response.data;

      if (data.code === ERROR_CODES.SUCCESS) {
        eventBus.emit(EVENTS.FETCH_MY_DOCUMENTS_SUCCESS, data);
      } else {
        eventBus.emit(EVENTS.FETCH_API_ERROR, data.code);
      }
    } catch (error) {
      let errorCode = error?.data?.code;
      if (!errorCode && error?.code === "ECONNABORTED") {
        errorCode = 30001;
      }
      eventBus.emit(EVENTS.FETCH_API_ERROR, errorCode);
    }
  };

  eventBus.on(EVENTS.FETCH_MY_DOCUMENTS, myDocumentshandler);

  // IMPORTANT CLEANUP
  return () => {
    eventBus.off(EVENTS.FETCH_MY_DOCUMENTS, myDocumentshandler);
  };
};

export const initializeDownloadDocumentsHandler = () => {
  const downloadDocumentsHandler = async (documentId) => {
    try {
      const response = await apiClient.get(
        `${API_PATHS.MY_DOCUMENTS.DOWNLOADS_MY_DOCUMENT}/${documentId}`,
        {
          responseType: "blob",
        },
      );
      const data = response.data;

      if (response.status === 200) {
        eventBus.emit(EVENTS.FETCH_MY_DOCUMENTS_DOWNLOAD_SUCCESS, data);
      } else {
        eventBus.emit(EVENTS.FETCH_API_ERROR, response.status);
      }
    } catch (error) {
      let errorCode = error?.data?.code;
      if (!errorCode && error?.code === "ECONNABORTED") {
        errorCode = 30001;
      }
      eventBus.emit(EVENTS.FETCH_API_ERROR, errorCode);
    }
  };

  eventBus.on(EVENTS.FETCH_MY_DOCUMENTS_DOWNLOAD, downloadDocumentsHandler);

  // IMPORTANT CLEANUP
  return () => {
    eventBus.off(EVENTS.FETCH_MY_DOCUMENTS_DOWNLOAD, downloadDocumentsHandler);
  };
};

export const initializeViewDocumentsHandler = () => {
  const viewDocumentsHandler = async (documentId) => {
    try {
      const response = await apiClient.get(
        `${API_PATHS.MY_DOCUMENTS.VIEW_MY_DOCUMENT}/${documentId}`,
        {
          responseType: "blob",
        },
      );
      const data = response.data;

      if (response.status === 200) {
        eventBus.emit(EVENTS.FETCH_MY_DOCUMENTS_VIEW_SUCCESS, data);
      } else {
        eventBus.emit(EVENTS.FETCH_API_ERROR, response.status);
      }
    } catch (error) {
      let errorCode = error?.data?.code;
      if (!errorCode && error?.code === "ECONNABORTED") {
        errorCode = 30001;
      }
      eventBus.emit(EVENTS.FETCH_API_ERROR, errorCode);
    }
  };

  eventBus.on(EVENTS.FETCH_MY_DOCUMENTS_VIEW, viewDocumentsHandler);

  // IMPORTANT CLEANUP
  return () => {
    eventBus.off(EVENTS.FETCH_MY_DOCUMENTS_VIEW, viewDocumentsHandler);
  };
};

export const initializeValidateUserHandler = () => {
  //eventBus.on(EVENTS.FETCH_VALIDATE_USER, async () => {
  const validateUserhandler = async () => {
    try {
      const response = await apiClient.get(API_PATHS.USER.VALIDATE_USER, {
        skipAuthValidation: true,
      });

      const data = response.data;
      if (data.code === ERROR_CODES.SUCCESS) {
        setUserData(data.data);
        eventBus.emit(EVENTS.FETCH_VALIDATE_USER_SUCCESS, data);
      } else {
        eventBus.emit(EVENTS.FETCH_API_ERROR, data.code);
      }
    } catch (error) {
      let errorCode = error?.data?.code;
      if (!errorCode && error?.code === "ECONNABORTED") {
        errorCode = 30001;
      }
      eventBus.emit(EVENTS.FETCH_API_ERROR, errorCode);
    }
  };

  eventBus.on(EVENTS.FETCH_VALIDATE_USER, validateUserhandler);

  // IMPORTANT CLEANUP
  return () => {
    eventBus.off(EVENTS.FETCH_VALIDATE_USER, validateUserhandler);
  };
};

export const initializeApiErrorHandler = (showError, t) => {
  const handler = (code) => {
    console.log("API Error Code Received:", code);
    const errorConfig = mapErrorToConfig(code, t);
    showError(errorConfig);
  };
  eventBus.on(EVENTS.FETCH_API_ERROR, handler);

  return () => {
    eventBus.off(EVENTS.FETCH_API_ERROR, handler);
  };
};
